import {
  ChronoUnit,
  DateTimeFormatter,
  Duration,
  Instant,
  LocalDate,
  LocalDateTime,
  LocalTime,
  OffsetDateTime,
  ZoneId,
} from "@js-joda/core"
import { Locale } from "@js-joda/locale"

export const formatDaysBetween = (start: Instant, end: Instant) => {
  const durationMillis = Duration.between(start, end).toMillis()
  const minutes = durationMillis / (60 * 1000)
  const hours = minutes / 60
  const days = hours / 24

  if (minutes < 1) {
    return "Just now"
  }

  if (minutes < 60) {
    const roundedMinutes = Math.round(minutes)
    return roundedMinutes === 1 ? "1 minute" : `${roundedMinutes} minutes`
  }

  if (hours < 24) {
    const roundedHours = Math.round(hours)
    return roundedHours === 1 ? "1 hour" : `${roundedHours} hours`
  }

  const roundedDays = Math.round(days)
  return roundedDays === 1 ? "1 day" : `${roundedDays} days`
}

export const formatDateTime = (dateTime: Instant, pattern: string) => {
  const localDateTime = LocalDateTime.ofInstant(dateTime, ZoneId.of("UTC")) // Adjust the time zone as needed
  const formatter = DateTimeFormatter.ofPattern(pattern).withLocale(Locale.US)
  return localDateTime.format(formatter)
}

export const formatTime = (time: LocalTime, pattern: string) => {
  const formatter = DateTimeFormatter.ofPattern(pattern).withLocale(Locale.US)
  return time.format(formatter)
}

export const formatDate = (date: LocalDate, pattern: string) => {
  const formatter = DateTimeFormatter.ofPattern(pattern).withLocale(Locale.US)
  return date.format(formatter)
}

// All datetime work below goes through @js-joda/core; never use `new Date()` here.

// js-joda's OffsetDateTime.toString() (like java.time) OMITS the seconds
// component when it is zero, producing e.g. "2026-07-09T12:08+02:00". The
// backend deserializes this field with kotlinx-datetime's ISO_DATE_TIME_OFFSET,
// which requires seconds and rejects the secondless form with 400 Bad Request.

// Always format with explicit seconds and a numeric "+HH:MM" offset.
const ISO_OFFSET_WITH_SECONDS = DateTimeFormatter.ofPattern("yyyy-MM-dd'T'HH:mm:ssxxx")

/**
 * Current local datetime as an ISO 8601 string with offset, second precision.
 * e.g. "2026-04-25T10:30:00+02:00"
 */
export const nowIso = (): string =>
  OffsetDateTime.now(ZoneId.systemDefault())
    .truncatedTo(ChronoUnit.SECONDS)
    .format(ISO_OFFSET_WITH_SECONDS)

/**
 * Parse a value the form fields hold (ISO 8601 with offset) into an
 * OffsetDateTime. Falls back to "now" when the value is empty.
 */
export const parseFormDateTime = (value: string | null | undefined): OffsetDateTime =>
  value
    ? OffsetDateTime.parse(value)
    : OffsetDateTime.now(ZoneId.systemDefault()).truncatedTo(ChronoUnit.SECONDS)

/**
 * Combine a "YYYY-MM-DD" date string and "HH:mm" time string with the supplied
 * offset, returning an ISO 8601 string with offset suitable for the form field.
 */
export const composeFormDateTime = (
  dateString: string,
  timeString: string,
  baseline: OffsetDateTime,
): string => {
  const time = LocalTime.parse(timeString.length === 5 ? `${timeString}:00` : timeString)
  return OffsetDateTime.of(LocalDate.parse(dateString).atTime(time), baseline.offset()).format(
    ISO_OFFSET_WITH_SECONDS,
  )
}
